import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Image } from "react-bootstrap";
import TypeWriter from "src/components/seek/typewriter";
import Truncate from "src/components/truncate";
import { FileService } from "src/utils/file.service";
import styles from "./chatAnswer.module.scss";
import AssetIcon from "src/components/asset/icon";
import _ from "lodash";
import Slider from "src/components/slider";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSeekEmailResponse,
  updateSeekFeedback,
} from "src/redux/actions/seek";
import cx from "classnames";
import AddToCollection from "src/pages/asset/components/addCollection";
import { useSeekAdoption } from "src/redux/actions/adoption/seek";
import { ASSET_DETAIL, SEEK } from "src/constants/adoption/sourceTypes";
import Text from "src/components/text";
import { SeekChat, SeekEventResponse } from "src/types/seek";
import Collapse from "src/pages/asset/components/animation/collapse";
import Markdown from "react-markdown";
import remarkGfm from 'remark-gfm'
import rehypeRaw from "rehype-raw";
import Thumbnail from "src/components/thumbnail";
import CitationTooltip from "./citationTooltip";
import { SEEK_USER_QUESTION_LIMIT_EXCEEDED } from "src/constants/seekErrors";


const LOADING_MESSAGES = [
  "Rummaging through the archives to unravel the mysteries of the universe...just a few more moments!",
  "Searching for answers in all documents, so you don't have to go full detective mode!",
  "Roses are red, violets are blue, searching for answers, just for you!",
  "Digging through the digital haystack, to find the needle of knowledge you seek!",
  "Putting on my detective hat and scouring the documents for clues to solve the mystery!",
];

const MAX_VISIBLE_ASSETS = 9;

/** Matches legacy seek strip / search seekResponse limit copy. */
const SEEK_VOLUME_LIMIT_EXCEEDED_MARKDOWN = `We understand that you'd like to SEEK more! Unfortunately, you've reached the permissible limit on the SEEK volume.

Fret not, we're here to assist! Please reach out to your [Customer Success Manager](mailto:support@paperflite.com) and they'll swiftly bring you back on track!`;

interface ChatAnswerProps {
  index: number;
  chat: any;
  profile: any;
  // eslint-disable-next-line no-unused-vars -- param name documents callback signature
  handleComplete: (index: number) => void;
  skipTypewriting: boolean;
  showDivider?: boolean;
  seekAsset?: boolean;
  scrollToBottom?: () => void;

  // eslint-disable-next-line no-unused-vars -- called when a follow-up question is clicked
  onAskFollowup?: (question: string) => void;
  /** When false, follow-up suggestions are hidden even if events include them. */
  showFollowUps?: boolean;
}

const ChatAnswer: React.FC<ChatAnswerProps> = ({
  index,
  chat,
  profile,
  handleComplete,
  skipTypewriting,
  showDivider = true,
  seekAsset,
  scrollToBottom,
  onAskFollowup,
  showFollowUps = false,
}) => {
  const dispatch = useDispatch();

  const [showAssets, setShowAssets] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [showAddToCollection, setShowAddToCollection] = useState(false);
  const [isHelpful, setIsHelpful] = useState<boolean | null>(null);
  const [hoveredCite, setHoveredCite] = useState<{
    citeIndex: number;
    targetEl: HTMLElement;
  } | null>(null);
  const answerContainerRef = useRef<HTMLDivElement>(null);
  const citeTooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const citeListenersCleanupRef = useRef<(() => void) | null>(null);

  const asset = useSelector((state: any) => state.asset.item);

  const seekEmailLoading = useSelector(
    (state: any) => state?.seek?.email?.loading ?? false,
  );
  
  const { trackSeekResponseReactedEvent, trackSeekDraftAnEmailClickedEvent } = useSeekAdoption();

  const chatError = chat?.error;
  const chatErrorCode = chatError?.code;
  const isUserQuestionLimitExceeded =
    chatErrorCode === SEEK_USER_QUESTION_LIMIT_EXCEEDED;

  /** Per-chat errors from failed answer stream; limit code uses legacy copy. */
  const answerErrorMessage = useMemo(() => {
    if (isUserQuestionLimitExceeded) {
      return SEEK_VOLUME_LIMIT_EXCEEDED_MARKDOWN;
    }
    return chatError?.message ? String(chatError.message) : "";
  }, [isUserQuestionLimitExceeded, chatError?.message]);

  const isSeekVolumeLimitError =
    Boolean(answerErrorMessage) && isUserQuestionLimitExceeded;

  const contents = useMemo(() => {
    return [...(chat.citations || [])].sort((a: any, b: any) => (a.page || 0) - (b.page || 0)).map((c: any) => c.content);
  }, [chat.citations]);

  const followUpQuestions = useMemo(() => {
    const events = chat.events || [];
    const followupEvents = events.filter((e: SeekEventResponse) => e.type === "follow_up");
    if (followupEvents.length === 0) return [];
    const sorted = [...followupEvents].sort((a, b) => (b.time || 0) - (a.time || 0));
    const latest = sorted[0];
    const questions = (latest?.payload?.follow_up_questions || []).map((q: any) => q.question);
    return Array.isArray(questions) ? questions : [];
  }, [chat.events]);

  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const rehypePlugins = useMemo(() => [rehypeRaw], []);

  useEffect(() => {
    if (answerErrorMessage) {
      setLoadingMessage(null);
      return;
    }
    if (chat.creating && !loadingMessage) {
      setLoadingMessage(
        LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)],
      );
      scrollToBottom?.();
    } else if (!chat.creating) {
      setLoadingMessage(null);
    }
  }, [chat.creating, loadingMessage, answerErrorMessage]);

  useEffect(() => {
    if (skipTypewriting) setShowAssets(true);
  }, [skipTypewriting]);

  useEffect(() => {
    if (showAssets) scrollToBottom?.();
  }, [showAssets]);

  useEffect(() => {
    setIsHelpful(chat.helpful);
  }, [chat.helpful]);

  useEffect(() => {
    const container = answerContainerRef.current;
    if (!container || !chat.answer) return;

    const getCiteIndex = (el: Element): number | null => {
      const dataCite = el.getAttribute("data-cite");
      if (dataCite !== null && dataCite !== "") {
        const n = parseInt(dataCite, 10);
        if (!Number.isNaN(n) && n >= 1) return n;
      }
      const text = el.textContent?.trim() ?? "";
      const n = parseInt(text, 10);
      if (Number.isNaN(n) || n < 1) return null;
      return n;
    };

    const id = setTimeout(() => {
      if (citeListenersCleanupRef.current) {
        citeListenersCleanupRef.current();
        citeListenersCleanupRef.current = null;
      }
      const cites = container.querySelectorAll(".seek__cite");
      const cleanupFns: Array<() => void> = [];

      cites.forEach((el) => {
        const element = el as HTMLElement;
        const onEnter = () => {
          if (citeTooltipHideTimeoutRef.current) {
            clearTimeout(citeTooltipHideTimeoutRef.current);
            citeTooltipHideTimeoutRef.current = null;
          }
          const idx = getCiteIndex(el);
          if (idx !== null) setHoveredCite({ citeIndex: idx, targetEl: element });
        };

        const onLeave = () => {
          citeTooltipHideTimeoutRef.current = setTimeout(() => {
            setHoveredCite(null);
            citeTooltipHideTimeoutRef.current = null;
          }, 150);
        };

        const onClick = (event: MouseEvent) => {
          const idx = getCiteIndex(el);

          if (seekAsset) {
            const pageContainer = document.querySelector(
              `#asset_viewer_${asset?.id} [data-page='${idx}']`,
            );

            if (!pageContainer) {
              return;
            }

            pageContainer.scrollIntoView(true);
          } else {
            if (idx === null) return;
            const content = contents[idx - 1];
            if (!content?.id) {
              return;
            }

            if (content?.id) {
              window.open(`/assets/${content.id}`, "_blank");
            }
          }
          event.preventDefault();
          event.stopPropagation();
        };
        element.addEventListener("mouseenter", onEnter);
        element.addEventListener("mouseleave", onLeave);
        element.addEventListener("click", onClick);
        cleanupFns.push(() => {
          element.removeEventListener("mouseenter", onEnter);
          element.removeEventListener("mouseleave", onLeave);
          element.removeEventListener("click", onClick);
        });
      });

      citeListenersCleanupRef.current = () => cleanupFns.forEach((fn) => fn());
    }, 0);

    return () => {
      clearTimeout(id);
      if (citeListenersCleanupRef.current) {
        citeListenersCleanupRef.current();
        citeListenersCleanupRef.current = null;
      }
      if (citeTooltipHideTimeoutRef.current) {
        clearTimeout(citeTooltipHideTimeoutRef.current);
        citeTooltipHideTimeoutRef.current = null;
      }
    };
  }, [chat.answer, contents]);

  const handleDraftEmail = () => {
    const shareableAssets = chat.citations?.filter((citation: any) => citation.content.settings?.share) ?? [];
    trackSeekDraftAnEmailClickedEvent({
      assetTypes: shareableAssets.map((c: any) => FileService.getFileType(c.content.metadata)),
      sourceCta: seekAsset ? ASSET_DETAIL : SEEK,
    });

    dispatch(fetchSeekEmailResponse({ chatId: String(chat.id!), id: chat.session }));
  };

  const handleFeedback = (helpful: boolean) => {
    if (helpful !== isHelpful) {
      setIsHelpful(helpful);
      dispatch(
        updateSeekFeedback({
          chatId: chat.id!,
          isHelpful: String(helpful),
        }),
      );
      const reactionType = helpful ? "Like" : "Dislike";
      const sourceCta = seekAsset ? ASSET_DETAIL : SEEK;
      trackSeekResponseReactedEvent({ reactionType, sourceCta });
    }
  };

  const hasShareableContents = contents?.some((c: any) => c.settings?.share);
  const hasContents = _.size(contents) > 0;
  const showSourcesSection = hasContents && !seekAsset;
  const showActionBar = !loadingMessage && !answerErrorMessage;
  const showFeedbackBar = !loadingMessage && !answerErrorMessage;

  return (
    <div key={index}>
      <div className={styles.questionContainer}>
        <Image src={profile.icon.thumbnail} className={styles.profileIcon} />
        <p className={cx(styles.question, seekAsset && styles.assetQuestion)}>
          {chat.question}
        </p>
      </div>
      {chat.showPlan && (
        <ReasoningProcess
          chat={chat}
          defaultExpanded={chat.creating}
          seekAsset={seekAsset}
        />
      )}

      {answerErrorMessage && (
        <div className={styles.answerContainer} ref={answerContainerRef}>
          <div className={cx(styles.answer, styles.markdown, seekAsset && styles.assetAnswer)}>
            <Markdown
              remarkPlugins={remarkPlugins}
              rehypePlugins={rehypePlugins}
              components={
                isSeekVolumeLimitError
                  ? {
                      a: ({ className, children, ...props }) => (
                        <a {...props} className={cx(className, styles.seekLimitLink)}>
                          {children}
                        </a>
                      ),
                    }
                  : undefined
              }
            >
              {answerErrorMessage}
            </Markdown>
          </div>
        </div>
      )}

      {chat.answer && (
        <div className={styles.answerContainer} ref={answerContainerRef}>
          <div className={cx(styles.answer, styles.markdown, seekAsset && styles.assetAnswer)}>
            <Markdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins}>{chat.answer}</Markdown>
          </div>
        </div>
      )}

      {!seekAsset && (
        <CitationTooltip
          hoveredCite={hoveredCite}
          contents={contents}
          onMouseEnter={() => {
            if (citeTooltipHideTimeoutRef.current) {
              clearTimeout(citeTooltipHideTimeoutRef.current);
              citeTooltipHideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => setHoveredCite(null)}
        />
      )}

      {showSourcesSection && (
        <>
          <div className={cx(styles.headerContainer, styles.citationsHeaderContainer)}>
            <p className={styles.citationsTitle}>Sources</p>
            <div className={styles.separator} />
          </div>
          {!seekAsset &&
            <div className={styles.citations}>
              {contents.map((content: any, index: number) => (
                <div className={styles.citation} key={content.id}>
                  <a
                    href={`/assets/${content.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.asset}
                  >
                    <div className={styles.assetIcon}>
                      <AssetIcon asset={content} />
                      <div className={styles.metadata}>
                        {FileService.getFileType(content.metadata)}
                      </div>
                    </div>
                    <div className={styles.assetDetails}>
                      <div className={styles.assetNumber}>{index + 1}.</div> <Text className={styles.assetTitle} text={content.name} lines={1} />
                    </div>
                  </a>
                </div>
              ))}
            </div>
          }
        </>
      )}
      <div className={styles.assetActions}>
        {showActionBar && (
          <>
            {hasShareableContents && (
              <span
                className={cx(
                  styles.action,
                  seekEmailLoading ? styles.selectedAction : styles.expandOnHover,
                )}
                onClick={() => !seekEmailLoading && handleDraftEmail()}
              >
                <i className={cx("paperflite-send", seekEmailLoading && styles.loading)} />
                <span className={cx(seekEmailLoading && styles.loading, styles.actionText)}>
                  {seekEmailLoading ? "drafting an email" : "draft an email"}
                </span>
              </span>
            )}
            {!seekAsset && hasContents && (
              <span
                className={cx(styles.action, !seekEmailLoading && styles.expandOnHover)}
                onClick={() => !seekEmailLoading && setShowAddToCollection(true)}
              >
                <i className={cx("paperflite-add", seekEmailLoading && styles.loading)} />
                <span className={cx(styles.actionText, seekEmailLoading && styles.loading)}>
                  add to collection
                </span>
              </span>
            )}
            {hasShareableContents && <div className={styles.divider} />}
          </>
        )}
        {showFeedbackBar && (
          <>
            <i
              onClick={() => !seekEmailLoading && handleFeedback(true)}
              className={cx(
                "paperflite-thumbs-up-unslected",
                seekEmailLoading && styles.loading,
                isHelpful === true && styles.selected,
              )}
            />
            <i
              onClick={() => !seekEmailLoading && handleFeedback(false)}
              className={cx(
                "paperflite-thumbs-down-unselected",
                seekEmailLoading && styles.loading,
                isHelpful === false && styles.selected,
              )}
            />
          </>
        )}
      </div>

      {showFollowUps && followUpQuestions.length > 0 && (
        <FollowupQuestions
          questions={followUpQuestions}
          onSelectQuestion={onAskFollowup || (() => { })}
        />
      )}

      <Slider.Dialog
        width={"380px"}
        show={showAddToCollection}
        onClose={() => setShowAddToCollection(false)}
      >
        {showAddToCollection && (
          <AddToCollection
            onClose={() => setShowAddToCollection(false)}
            show={showAddToCollection}
            assets={contents}
            sourceType="Seek"
          />
        )}
      </Slider.Dialog>
      {showDivider && showFeedbackBar && <div className={styles.divider} />}
    </div>
  );
};

interface FollowupQuestionsProps {
  questions: string[];
  onSelectQuestion: (question: string) => void;
}

const FollowupQuestions: React.FC<FollowupQuestionsProps> = ({
  questions,
  onSelectQuestion,
}) => (
  <div className={styles.followupQuestions}>
    <div className={cx(styles.headerContainer, styles.followupQuestionsHeader)}>
      <p className={styles.followupQuestionsTitle}>Follow up questions</p>
      <div className={styles.separator} />
    </div>
    <div className={styles.followupQuestionsList}>
      {questions.map((question, i) => (
        <button
          key={i}
          type="button"
          className={styles.followupQuestionItem}
          onClick={() => onSelectQuestion(question)}
        >
          <i className="fa-solid fa-arrow-turn-up fa-rotate-90"></i>
          <span className={styles.followupQuestionText}>{question}</span>
        </button>
      ))}
    </div>
  </div>
);

interface ReasoningProcessProps {
  chat: SeekChat;
  defaultExpanded: boolean;
  seekAsset?: boolean;
}

type StepTypeToRender = "planning" | "search_asset" | "response";
const RESPONSE_EVENT_TYPES = ["answer", "critique_answer", "final_answer"];

const ReasoningProcess: React.FC<ReasoningProcessProps> = ({
  chat,
  defaultExpanded = true,
  seekAsset,
}) => {
  const events = chat.events || [];
  const [expanded, setExpanded] = useState(defaultExpanded);

  const orderedStepTypes = useMemo(() => {
    const sorted = [...events].sort((a, b) => (a.time || 0) - (b.time || 0));
    const seen = new Set<string>();
    const result: StepTypeToRender[] = [];
    for (const e of sorted) {
      const t = e.type;
      if (!t) continue;
      if (t === "planning" && !seen.has("planning")) {
        seen.add("planning");
        result.push("planning");
      } else if (
        t === "search_asset" &&
        !seekAsset &&
        !seen.has("search_asset")
      ) {
        seen.add("search_asset");
        result.push("search_asset");
      } else if (RESPONSE_EVENT_TYPES.includes(t) && !seen.has("response")) {
        seen.add("response");
        result.push("response");
      }
    }
    return result;
  }, [events, seekAsset]);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => (a.time || 0) - (b.time || 0)),
    [events]
  );
  const eventsByType = useMemo(() => {
    const map: Record<string, SeekEventResponse[]> = {};
    for (const e of sortedEvents) {
      if (!map[e.type]) map[e.type] = [];
      map[e.type].push(e);
    }
    return map;
  }, [sortedEvents]);

  const hasFinalAnswer = useMemo(
    () => (eventsByType["final_answer"]?.length ?? 0) > 0,
    [eventsByType]
  );

  const latestResponseStepType = useMemo<"answer" | "critique_answer" | null>(() => {
    if (hasFinalAnswer) return null;

    const answerEvents = eventsByType["answer"] || [];
    const critiqueEvents = eventsByType["critique_answer"] || [];
    const maxAnswerTime = answerEvents.length ? Math.max(...answerEvents.map((e) => e.time || 0)) : 0;
    const maxCritiqueTime = critiqueEvents.length ? Math.max(...critiqueEvents.map((e) => e.time || 0)) : 0;

    if (maxAnswerTime >= maxCritiqueTime && answerEvents.length > 0) return "answer";

    if (critiqueEvents.length > 0) return "critique_answer";
    return null;
  }, [eventsByType, hasFinalAnswer]);

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const renderStep = React.useCallback((stepType: StepTypeToRender) => {
    switch (stepType) {
      case "planning": {
        const steps = eventsByType["planning"] || [];
        if (steps.length === 0) return null;
        return (
          <ReasoningThoughtStep
            step={steps[0]}
            label="Thinking through your question"
          />
        );
      }
      case "search_asset": {
        const steps = eventsByType["search_asset"] || [];
        if (steps.length === 0) return null;
        return (
          <ReasoningSearchAssetsStep
            steps={steps}
            label="Searching relevant insights"
          />
        );
      }
      case "response":
        if (hasFinalAnswer) {
          return <ReasoningResponseGeneratedStep />;
        }
        if (latestResponseStepType === "answer") {
          return (
            <ReasoningAnswerStep
              steps={eventsByType["answer"] || []}
              label="Crafting your response"
            />
          );
        }
        if (latestResponseStepType === "critique_answer") {
          return (
            <ReasoningCritiqueStep
              steps={eventsByType["critique_answer"] || []}
              label="Reviewing your response"
            />
          );
        }
        return null;
      default:
        return null;
    }
  }, [eventsByType, hasFinalAnswer, latestResponseStepType]);

  const displayedStepsCount = useMemo(() => {
    let count = 0;
    for (const stepType of orderedStepTypes) {
      if (stepType === "planning" && (eventsByType["planning"]?.length ?? 0) > 0) count += 1;
      else if (stepType === "search_asset" && (eventsByType["search_asset"]?.length ?? 0) > 0) count += 1;
      else if (stepType === "response" && (hasFinalAnswer || latestResponseStepType !== null)) count += 1;
    }
    return count;
  }, [orderedStepTypes, eventsByType, hasFinalAnswer, latestResponseStepType]);

  const headerTitle = hasFinalAnswer && displayedStepsCount > 0
    ? `${displayedStepsCount} steps completed`
    : "Thinking through";

  return (
    <div className={cx(styles.thoughtProcess, {
      [styles.expanded]: expanded,
      [styles.hasFinalAnswer]: hasFinalAnswer
    })}>
      <div className={styles.header} onClick={() => setExpanded(!expanded)}>
        <p className={styles.title}>{headerTitle}</p>
        {hasFinalAnswer && (
          <span className={cx(styles.headerChevron, 'paperflite-dropdown')}></span>
        )}
        <div className={styles.separator} />
      </div>
      <Collapse isOpen={expanded}>
        <div className={styles.content}>
          {events.length > 0 && orderedStepTypes.map((stepType) => (
            <React.Fragment key={stepType}>{renderStep(stepType)}</React.Fragment>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

interface ReasoningThoughtStepProps {
  step: SeekEventResponse;
  label: string;
}

const ReasoningThoughtStep: React.FC<ReasoningThoughtStepProps> = ({ step, label }) => {
  return (
    <div className={cx(styles.reasoningStep, styles.thoughtStep)}>
      <div className={styles.left}>
        <div className={styles.stepDot} />
        <div className={styles.line} />
      </div>
      <div className={styles.right}>
        <p className={styles.stepTitle}>{label}</p>
        <p className={styles.stepContent}>{step.payload}</p>
      </div>
    </div>
  );
};

interface ReasoningSearchAssetsStepProps {
  steps: SeekEventResponse[];
  label: string;
  isActive?: boolean;
}

const ReasoningSearchAssetsStep: React.FC<ReasoningSearchAssetsStepProps> = ({ steps, label }) => {
  const assets = useMemo(() => {
    const seen = new Set<string>();
    const result: any[] = [];
    for (const step of steps) {
      const items = step?.payload?.assets || [];
      for (const asset of items) {
        if (asset?.id && !seen.has(asset.id)) {
          seen.add(asset.id);
          result.push(asset);
        }
      }
    }
    return result;
  }, [steps]);

  const hasComplete = steps.some((e: SeekEventResponse) => e.completed === true);
  const visibleAssets = assets.slice(0, MAX_VISIBLE_ASSETS);
  const remainingCount = Math.max(0, assets.length - MAX_VISIBLE_ASSETS);

  return (
    <div className={cx(styles.reasoningStep, styles.searchAssetsStep)}>
      <div className={styles.left}>
        {hasComplete ? <div className={styles.stepDot} /> : <div className={styles.spinner} />}
        <div className={styles.line} />
      </div>
      <div className={styles.right}>
        <p className={styles.stepTitle}>{label}</p>
        <div className={styles.stepContent}>
          {visibleAssets.map((asset: any) => {
            const href = `/assets/${asset.id}`;
            return (
              <a
                key={asset.id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.assetPill}
              >
                <Thumbnail
                  media={asset.icon}
                  width={20}
                  className={cx(styles.assetIcon)}
                  alt=""
                  defaultImage="/images/assets/default_asset.jpg"
                />
                <Text className={styles.assetName} text={asset.name || "Untitled"} lines={1} tooltip />
              </a>
            );
          })}
          {remainingCount > 0 && (
            <span className={cx(styles.assetPill, styles.moreAssets)}>+ {remainingCount} more assets</span>
          )}
        </div>
      </div>
    </div>
  );
};

interface ReasoningAgentStepProps {
  steps: SeekEventResponse[];
  label: string;
}

const ReasoningAnswerStep: React.FC<ReasoningAgentStepProps> = ({ steps, label }) => {
  const allComplete = steps.length > 0 && steps.every((e) => e.completed === true);
  return (
    <div className={cx(styles.reasoningStep, styles.craftingStep)}>
      <div className={styles.left}>
        {allComplete ? <div className={styles.stepDot} /> : <div className={styles.spinner} />}
        <div className={styles.line} />
      </div>
      <div className={styles.right}>
        <p className={styles.stepTitle}>{label}</p>
      </div>
    </div>
  );
};

const ReasoningCritiqueStep: React.FC<ReasoningAgentStepProps> = ({ steps, label }) => {
  const allComplete = steps.length > 0 && steps.every((e) => e.completed === true);
  return (
    <div className={cx(styles.reasoningStep, styles.craftingStep)}>
      <div className={styles.left}>
        {allComplete ? <div className={styles.stepDot} /> : <div className={styles.spinner} />}
        <div className={styles.line} />
      </div>
      <div className={styles.right}>
        <p className={styles.stepTitle}>{label}</p>
      </div>
    </div>
  );
};

const ReasoningResponseGeneratedStep: React.FC = () => {
  return (
    <div className={cx(styles.reasoningStep, styles.craftingStep)}>
      <div className={styles.left}>
        <div className={styles.stepDot} />
        <div className={styles.line} />
      </div>
      <div className={styles.right}>
        <p className={styles.stepTitle}>Response Generated</p>
      </div>
    </div>
  );
};

export default ChatAnswer;
